import { clamp, damp, normalizeAngle } from './math.js';

export const FLIGHT_LIMITS = Object.freeze({
  stallSpeed: 38,
  rotateSpeed: 51,
  maxSpeed: 137,
  safeLandingMinSpeed: 42,
  safeLandingMaxSpeed: 88,
  safeSinkRate: -7.5,
  maxPitch: 0.52,
  maxRoll: 1.08
});

export function createFlightState(overrides = {}) {
  return {
    position: { x: 0, y: 4.6, z: 650 },
    speed: 0,
    throttle: 0.08,
    pitch: 0,
    roll: 0,
    heading: 0,
    verticalSpeed: 0,
    grounded: true,
    airborneEver: false,
    crashed: false,
    landed: false,
    stall: false,
    touchdown: null,
    distance: 0,
    gForce: 1,
    gearDown: true,
    ...overrides
  };
}

export function isSafeTouchdown(state) {
  return (
    state.speed >= FLIGHT_LIMITS.safeLandingMinSpeed &&
    state.speed <= FLIGHT_LIMITS.safeLandingMaxSpeed &&
    state.verticalSpeed >= FLIGHT_LIMITS.safeSinkRate &&
    Math.abs(state.roll) <= 0.24 &&
    state.pitch >= -0.1 &&
    state.pitch <= 0.3
  );
}

export function updateFlight(state, controls, dt, environment = {}) {
  if (state.crashed || state.landed) return state;

  const step = clamp(dt, 0, 0.05);
  const performance = environment.performance ?? {};
  const maxSpeed = performance.maxSpeed ?? FLIGHT_LIMITS.maxSpeed;
  const acceleration = performance.acceleration ?? 1;
  const turnRate = performance.turnRate ?? 1;
  const climbRate = performance.climbRate ?? 1;
  const groundHeight = environment.groundHeight ?? 0;
  const runway = Boolean(environment.isRunway);
  state.touchdown = null;

  state.throttle = clamp(
    state.throttle + (controls.throttle ?? 0) * 0.34 * step,
    0,
    1
  );

  const pitchAuthority = state.grounded ? clamp(state.speed / 48, 0.12, 1) : 1;
  const pitchTarget = (controls.pitch ?? 0) * FLIGHT_LIMITS.maxPitch * pitchAuthority;
  const rollTarget = state.grounded ? 0 : (controls.roll ?? 0) * FLIGHT_LIMITS.maxRoll;
  state.pitch = damp(state.pitch, pitchTarget, state.grounded ? 2.8 : 3.2, step);
  state.roll = damp(state.roll, rollTarget, state.grounded ? 5 : 3.6, step);

  if (state.grounded) {
    const rollingDrag = state.speed > 0.2 ? 4.4 : 0;
    const thrust = state.throttle * 35 * acceleration - rollingDrag - state.speed * 0.055;
    state.speed = clamp(state.speed + thrust * step, 0, maxSpeed);
    state.heading = normalizeAngle(
      state.heading + (controls.yaw ?? 0) * (0.12 + state.speed * 0.0018) * turnRate * step
    );
    state.verticalSpeed = 0;
    state.position.y = groundHeight + 4.6;

    if (
      runway &&
      state.speed >= FLIGHT_LIMITS.rotateSpeed &&
      state.pitch > 0.055 &&
      state.throttle > 0.58
    ) {
      state.grounded = false;
      state.airborneEver = true;
      state.verticalSpeed = 4.8;
    }
  } else {
    const coordinatedTurn = Math.sin(state.roll) * (0.14 + state.speed * 0.00225);
    state.heading = normalizeAngle(
      state.heading + (coordinatedTurn + (controls.yaw ?? 0) * 0.2) * turnRate * step
    );

    const targetSpeed = 34 + state.throttle * Math.max(50, maxSpeed - 34);
    const pitchDrag = Math.abs(state.pitch) * 8 + Math.abs(state.roll) * 2.4;
    state.speed = clamp(
      state.speed + (targetSpeed - state.speed - pitchDrag) * 0.34 * step,
      18,
      maxSpeed
    );

    state.stall = state.speed < FLIGHT_LIMITS.stallSpeed && state.pitch > 0.08;
    const lift = clamp((state.speed - 27) / 38, 0, 1.25);
    let desiredVerticalSpeed = (Math.sin(state.pitch) * state.speed * 0.78 + (lift - 0.62) * 5.8) * climbRate;
    if (state.stall) desiredVerticalSpeed -= (FLIGHT_LIMITS.stallSpeed - state.speed) * 0.72 + 5;
    state.verticalSpeed = damp(state.verticalSpeed, desiredVerticalSpeed, state.stall ? 1.8 : 1.1, step);
    state.gForce = clamp(1 + Math.abs(state.roll) * 0.38 + Math.abs(state.verticalSpeed) * 0.018, 0.4, 3.5);
  }

  const horizontalSpeed = state.speed * Math.max(0.25, Math.cos(state.pitch));
  const dx = -Math.sin(state.heading) * horizontalSpeed * step;
  const dz = -Math.cos(state.heading) * horizontalSpeed * step;
  state.position.x += dx;
  state.position.z += dz;
  state.position.y += state.verticalSpeed * step;
  state.distance += Math.hypot(dx, dz);

  const gearHeight = groundHeight + 4.55;
  if (!state.grounded && state.position.y <= gearHeight) {
    const safe = runway && isSafeTouchdown(state);
    state.touchdown = {
      safe,
      runwayId: environment.runwayId ?? null,
      sinkRate: state.verticalSpeed,
      speed: state.speed
    };
    state.position.y = gearHeight;
    if (safe) {
      state.grounded = true;
      state.landed = environment.runwayId === 'landing';
      state.verticalSpeed = 0;
      state.pitch = Math.max(0, state.pitch * 0.5);
    } else {
      state.crashed = true;
      state.speed *= 0.3;
      state.verticalSpeed = 0;
    }
  }

  return state;
}
